import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AdminLoginsReportPage from './AdminLoginsReportPage';
import AdminSignupsReportPage from './AdminSignupsReportPage';
import CoachBehaviorReport from './reports/CoachBehaviorReport';
import FeatureScorecardReport from './reports/FeatureScorecardReport';
import ProfileBuildPathReport from './reports/ProfileBuildPathReport';
import ConversionReport from './reports/ConversionReport';
import ActivationReport from './reports/ActivationReport';
import CampaignsReport from './reports/CampaignsReport';
import TapHeatmapReport from './reports/TapHeatmapReport';
import TradingCardsReport, { TC_WINDOWS } from './reports/TradingCardsReport';
import '../components/Layout.css';
import Button from '../components/Button';
import AdminPage, { AdminWindowSwitcher } from '../components/AdminShell';
import './AdminReportsPage.css';

// Tabs the page renders, in display order, one array per row of the strip.
// Keys appear in the URL as ?tab=.
//
// Rows (David, 2026-11-05): the daily numbers first, then outreach, then the
// rest. TABS[0] is also the page's DEFAULT tab (see `activeTab` below) —
// that is derived from this list rather than hardcoded, so reordering here
// moves the landing tab with it.
const TAB_ROWS = [
  [
    { key: 'signups',           label: 'Signups' },
    { key: 'logins',            label: 'Logins' },
    { key: 'active-users',      label: 'Active Users' },
    { key: 'trading-cards',     label: 'Trading Cards' },
  ],
  [
    { key: 'activation',        label: 'Activation' },
    { key: 'profile-build',     label: 'Profile Build Path' },
  ],
  [
    { key: 'coach-behavior',    label: 'Coach Behavior' },
    { key: 'feature-scorecard', label: 'Feature Scorecard' },
    { key: 'conversion',        label: 'Conversion & Drop-off' },
    { key: 'tap-heatmap',       label: 'Tap Heatmap' },
    { key: 'channels',          label: 'Channels' },
    { key: 'funnel',            label: 'Funnel' },
  ],
];
// Rendered here but reached from the rail, not the strip: Campaigns lives under Marketing (David,
// 2026-11-05). ?tab=campaigns still works, lights the rail's Campaigns item, and hides the strip.
const RAIL_TABS = [
  { key: 'campaigns',         label: 'Campaigns', title: 'Campaigns' },
];
const TABS = [...TAB_ROWS.flat(), ...RAIL_TABS];
const railTab = (key) => RAIL_TABS.find((t) => t.key === key);

// Palette for the stacked-bar / channel pills. Cycles by index so we don't
// need a per-channel-key map — keys come from the server and can change.
const CHANNEL_COLORS = ['#4D8064', '#16a34a', '#f97316', '#a855f7', '#14b8a6', '#dc2626', '#0ea5e9', '#84cc16'];

const formatRelative = (iso) => {
  if (!iso) return '—';
  // Backend serializes UTC timestamps with no timezone suffix (e.g.
  // "2026-05-31T17:45:00"). JS parses a tz-less datetime as LOCAL time, which
  // for any admin behind UTC pushed the value into the future and produced
  // negative "ago" readings. Treat a tz-less string as UTC by appending 'Z'.
  const hasTz = /([zZ])|([+-]\d{2}:?\d{2})$/.test(iso);
  const then = new Date(hasTz ? iso : `${iso}Z`);
  // Tiered relative format — minutes are unreadable past an hour or two, so
  // roll up into hours/days/weeks/months. Clamp at 0 to avoid negative "ago".
  const seconds = Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const remMin = minutes % 60;
    return remMin ? `${hours}h ${remMin}m ago` : `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    const remHr = hours % 24;
    return remHr ? `${days}d ${remHr}h ago` : `${days}d ago`;
  }
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

const ROLE_LABELS = {
  athlete: 'Athlete',
  base_user: 'Base User',
  player_parent: 'Athlete',
  coach: 'Coach',
  basic_coach: 'Coach (Pending)',
  partner: 'Partner',
  guardian: 'Guardian',
  admin: 'Admin',
  internal_staff: 'Internal Staff',
  marketing: 'Marketing',
};

// These style objects are applied at ~40 JSX call sites across this file, so
// they are retargeted onto the dashboard's look rather than replaced one usage
// at a time.
//
// The values MIRROR the `--adm-*` tokens in AdminDashboard.css (that file stays
// the source of truth; change it and change ADM here). They are written out
// literally rather than as `var(--adm-*)` on purpose: real browsers resolve a
// var() assigned through CSSOM — measured, Chrome returns 22px — but jsdom's
// cssstyle silently DROPS the whole declaration, so under jest every one of
// these cards renders unpadded and untinted. That makes the page impossible to
// inspect in a rendered test, which is how the padding loss was caught here in
// the first place. Literals behave identically in both.
const ADM = {
  ink: '#07131F',
  green: '#3F6B53',
  text: '#4A5867',
  muted: '#8CA096',
  bg: '#EEF2F0',
  hairline: '#F5F8F6',
  radius: 18,
  pad: 22,
  gap: 16,
  shadow: '0 1px 3px rgba(7, 19, 31, 0.05)',
};

const cardStyle = {
  background: '#fff',
  borderRadius: ADM.radius,
  boxShadow: ADM.shadow,
  padding: ADM.pad,
  marginBottom: ADM.gap,
};

const tileLabelStyle = {
  fontSize: 10.5,
  fontWeight: 700,
  color: ADM.muted,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: 6,
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const thStyle = {
  textAlign: 'left',
  padding: '9px 12px',
  borderBottom: `1px solid ${ADM.bg}`,
  fontSize: 10.5,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: ADM.muted,
  fontWeight: 700,
};

const tdStyle = {
  padding: '10px 12px',
  borderBottom: `1px solid ${ADM.hairline}`,
  color: ADM.text,
};

const sectionTitleStyle = {
  fontSize: 15,
  fontWeight: 700,
  marginBottom: 16,
  color: ADM.ink,
};

export default function AdminReportsPage() {
  const { user, API_URL } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeUsers, setActiveUsers] = useState(null);
  // Trading Cards window (?days= on its endpoint). Lives here, not in the
  // tab, because its switcher sits in the page header.
  const [tcDays, setTcDays] = useState(7);
  const [channels, setChannels] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [funnelDays, setFunnelDays] = useState(30);
  const [channelsRole, setChannelsRole] = useState('all');
  // When an admin clicks a guardian row in Active Users we resolve their
  // linked athlete(s) into this modal rather than navigating to the guardian's
  // own (empty) athlete profile. null = closed.
  const [guardianLinks, setGuardianLinks] = useState(null);

  // Active tab driven by URL so home-page deep-links (?tab=channels) land
  // right on the tab. Falls back to coach-behavior if param missing/unknown.
  const urlTab = searchParams.get('tab');
  const activeTab = TABS.find((t) => t.key === urlTab) ? urlTab : TABS[0].key;

  const setActiveTab = (key) => {
    searchParams.set('tab', key);
    setSearchParams(searchParams, { replace: true });
  };

  const getConfig = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  // Open the linked-athletes picker for a guardian row. A guardian has no
  // athlete profile of their own, so /admin/athletes/{id} would silently
  // materialize an empty one — instead surface the athlete(s) they're linked
  // to and let the admin jump to a real profile.
  const openGuardianLinks = useCallback(async (u) => {
    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
    setGuardianLinks({ guardian: u, guardianName: name, loading: true, athletes: [], error: '' });
    try {
      const res = await axios.get(`${API_URL}/admin/users/${u.user_id}/linked`, getConfig());
      setGuardianLinks({
        guardian: u,
        guardianName: name,
        loading: false,
        athletes: res.data?.linked_athletes || [],
        error: '',
      });
    } catch (e) {
      setGuardianLinks({
        guardian: u,
        guardianName: name,
        loading: false,
        athletes: [],
        error: 'Failed to load linked athletes.',
      });
    }
  }, [API_URL, getConfig]);

  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'internal_staff') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Load the remaining original reports together — same as before, they share
  // the top-of-page state. Channels/Funnel are loaded lazily when
  // their tab is shown so we don't pay for them on first paint.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        // Trading Cards fetches its own data (TradingCardsReport), so a
        // failure there no longer blanks every tab.
        const usersRes = await axios.get(`${API_URL}/admin/reports/active-users?limit=50`, getConfig());
        if (cancelled) return;
        setActiveUsers(usersRes.data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || 'Failed to load reports');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [API_URL, getConfig]);

  // Lazy-load the v2 reports the first time the user clicks each tab.
  useEffect(() => {
    let cancelled = false;
    const loadV2 = async () => {
      try {
        if (activeTab === 'channels') {
          const roleParam = channelsRole && channelsRole !== 'all' ? `?role=${encodeURIComponent(channelsRole)}` : '';
          const r = await axios.get(`${API_URL}/admin/reports/channels${roleParam}`, getConfig());
          if (!cancelled) setChannels(r.data);
        } else if (activeTab === 'funnel') {
          const r = await axios.get(`${API_URL}/admin/reports/funnel?days=${funnelDays}`, getConfig());
          if (!cancelled) setFunnel(r.data);
        }
      } catch (err) {
        // Don't blow up the whole page — the tab just shows an empty state
        if (!cancelled) console.error('v2 report load failed', err);
      }
    };
    loadV2();
    return () => { cancelled = true; };
  }, [activeTab, funnelDays, channelsRole, API_URL, getConfig]);

  // Rendered inside the shell (below) rather than short-circuiting the whole
  // component — an early return here dropped the sidebar and page title on
  // every load, so the frame flashed in and out.
  const preamble = loading
    ? <div className="adm__card"><div className="adm-loading">Loading reports…</div></div>
    : error
      ? <div className="adm-error">{error}</div>
      : null;

  const isTradingCards = activeTab === 'trading-cards';
  const onRail = railTab(activeTab);

  return (
    <AdminPage
      active={onRail ? onRail.key : 'reports'}
      title={onRail ? onRail.title : 'Reports'}
      subtitle={isTradingCards
        ? 'Trading cards · who built a card most recently'
        : onRail ? undefined : TABS.find((x) => x.key === activeTab)?.label}
      actions={isTradingCards
        ? <AdminWindowSwitcher options={TC_WINDOWS} value={tcDays} onChange={setTcDays} />
        : undefined}
    >
      {/* Tab strip — the shell's pill tabs, matching the dashboard's window
          switcher. Previously the settings-tabs chrome from Layout.css, which
          is a different visual language from everything around it here. */}
      {!onRail && <div className="adm-tabs rpt-tabrows" role="tablist">
        {TAB_ROWS.map((row) => (
          <div key={row[0].key} className="rpt-tabrow">
            {row.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={activeTab === t.key}
                className={`adm-tab${activeTab === t.key ? ' adm-tab--active' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        ))}
      </div>}

      {preamble}
      {!loading && !error && (
      <>

      {activeTab === 'coach-behavior' && <CoachBehaviorReport />}
      {activeTab === 'feature-scorecard' && <FeatureScorecardReport />}
      {activeTab === 'profile-build' && <ProfileBuildPathReport />}
      {activeTab === 'conversion' && <ConversionReport />}
      {activeTab === 'tap-heatmap' && <TapHeatmapReport />}
      {activeTab === 'activation' && <ActivationReport />}

      {activeTab === 'active-users' && (
      <div style={cardStyle}>
        <div style={sectionTitleStyle}>Recently active users</div>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>User</th>
              <th style={thStyle}>Role</th>
              <th style={thStyle}>Last active</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Events 7d</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Events 30d</th>
            </tr>
          </thead>
          <tbody>
            {(activeUsers?.users || []).map((u) => {
              const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
              const isGuardian = u.role === 'guardian';
              return (
                <tr
                  key={u.user_id}
                  onClick={() => (isGuardian ? openGuardianLinks(u) : navigate(`/admin/athletes/${u.user_id}`))}
                  style={{ cursor: 'pointer' }}
                  title={isGuardian ? `View athletes linked to ${name}` : `View ${name}`}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
                >
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, color: ADM.green }}>{name}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{u.email}</div>
                  </td>
                  <td style={tdStyle}>
                    {ROLE_LABELS[u.role] || u.role}
                    {isGuardian && (
                      <span style={{ marginLeft: 6, fontSize: 11, color: '#7c3aed' }}>→ linked athletes</span>
                    )}
                  </td>
                  <td style={tdStyle}>{formatRelative(u.last_active_at)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{u.events_7d}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{u.events_30d}</td>
                </tr>
              );
            })}
            {(!activeUsers || activeUsers.users.length === 0) && (
              <tr><td style={tdStyle} colSpan={5}>No active users yet — last_active_at will populate as iOS clients hit the API.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {isTradingCards && <TradingCardsReport days={tcDays} />}

      {activeTab === 'channels' && (
        <ChannelsTab
          channels={channels}
          role={channelsRole}
          onRoleChange={setChannelsRole}
          onView={(key) => navigate(`/admin/users?bucket=channel:${encodeURIComponent(key)}`)}
        />
      )}

      {activeTab === 'campaigns' && <CampaignsReport />}

      {activeTab === 'funnel' && (
        <FunnelTab funnel={funnel} days={funnelDays} onDaysChange={setFunnelDays} />
      )}

      {/* Signups + Logins reports are self-contained page components; render
          them inline as tabs so they live in the reports menu. */}
      {activeTab === 'signups' && (
        <AdminSignupsReportPage embedded />
      )}

      {activeTab === 'logins' && (
        <AdminLoginsReportPage embedded />
      )}

      {guardianLinks && (
        <div
          onClick={() => setGuardianLinks(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12, width: 'min(440px, 92vw)',
              maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
            }}
          >
            {/* Guardian's own info */}
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', color: '#7c3aed' }}>Guardian</div>
                <div style={{ fontWeight: 600, color: '#0f172a', marginTop: 3 }}>{guardianLinks.guardianName}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>{guardianLinks.guardian?.email}</div>
                {guardianLinks.guardian?.last_active_at && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                    Last active {formatRelative(guardianLinks.guardian.last_active_at)} · {guardianLinks.guardian.events_30d ?? 0} events / 30d
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                iconOnly
                onClick={() => setGuardianLinks(null)}
                aria-label="Close"
              >×</Button>
            </div>
            {/* Linked athletes */}
            <div style={{ padding: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', color: '#64748b', marginBottom: 10 }}>
                Linked athlete{guardianLinks.athletes.length === 1 ? '' : 's'}
              </div>
              {guardianLinks.loading && (
                <div style={{ padding: 8, color: '#64748b', fontSize: 14 }}>Loading…</div>
              )}
              {!guardianLinks.loading && guardianLinks.error && (
                <div style={{ padding: 8, color: '#dc2626', fontSize: 14 }}>{guardianLinks.error}</div>
              )}
              {!guardianLinks.loading && !guardianLinks.error && guardianLinks.athletes.length === 0 && (
                <div style={{ padding: 8, color: '#64748b', fontSize: 14 }}>No athletes linked to this guardian.</div>
              )}
              {!guardianLinks.loading && !guardianLinks.error && guardianLinks.athletes.map((a) => (
                <div
                  key={a.user_id}
                  style={{
                    border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 10,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{a.name}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{a.email}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
                      {a.relation ? <span style={{ textTransform: 'capitalize' }}>{a.relation}</span> : null}
                      {a.relation && a.subscription_status ? ' · ' : null}
                      {a.subscription_status ? `Subscription: ${a.subscription_status}` : null}
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => { setGuardianLinks(null); navigate(`/admin/athletes/${a.user_id}`); }}
                    style={{ whiteSpace: 'nowrap' }}
                    title={`Open ${a.name}'s full profile`}
                  >
                    View athlete details →
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </AdminPage>
  );
}

/* ── Channels Tab ──────────────────────────────────────── */

const ROLE_FILTER_OPTIONS = [
  { value: 'all', label: 'All roles' },
  { value: 'base_user', label: 'Base users (free signups)' },
  { value: 'player_parent', label: 'Athletes (paid)' },
  { value: 'coach', label: 'Coaches' },
  { value: 'basic_coach', label: 'Basic coaches (pending)' },
  { value: 'partner', label: 'Partners' },
  { value: 'guardian', label: 'Guardians' },
  { value: 'admin', label: 'Admins' },
  { value: 'internal_staff', label: 'Internal staff' },
];

function ChannelsTab({ channels, role, onRoleChange, onView }) {
  const RoleDropdown = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      <label style={{ fontSize: 13, color: '#64748b' }}>Filter by role:</label>
      <select
        value={role || 'all'}
        onChange={(e) => onRoleChange(e.target.value)}
        style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }}
      >
        {ROLE_FILTER_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );

  if (!channels) {
    return (
      <div style={cardStyle}>
        <div style={sectionTitleStyle}>Acquisition channels</div>
        {RoleDropdown}
        <div>Loading channels…</div>
      </div>
    );
  }
  const list = channels.channels || [];
  if (list.length === 0) {
    return (
      <div style={cardStyle}>
        <div style={sectionTitleStyle}>Acquisition channels</div>
        {RoleDropdown}
        <div>No channel data for this role.</div>
      </div>
    );
  }
  const total = channels.total || list.reduce((s, c) => s + (c.count || 0), 0) || 1;
  const top6 = [...list].sort((a, b) => b.count - a.count).slice(0, 6);

  return (
    <div style={cardStyle}>
      <div style={sectionTitleStyle}>Acquisition channels</div>
      {RoleDropdown}

      {/* Stacked bar of the top 6 channels. CSS-only — flex children sized
          by percent. The legend below names each band. */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', width: '100%', height: 20, borderRadius: 6, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          {top6.map((c, i) => {
            const pct = (c.count / total) * 100;
            if (pct < 0.1) return null;
            return (
              <div
                key={c.key}
                style={{ width: `${pct}%`, background: CHANNEL_COLORS[i % CHANNEL_COLORS.length] }}
                title={`${c.label}: ${c.count} (${pct.toFixed(1)}%)`}
              />
            );
          })}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 10, fontSize: 12, color: '#475569' }}>
          {top6.map((c, i) => (
            <div key={c.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: CHANNEL_COLORS[i % CHANNEL_COLORS.length], display: 'inline-block' }} />
              {c.label}
            </div>
          ))}
        </div>
      </div>

      <table style={{ ...tableStyle, marginTop: 20 }}>
        <thead>
          <tr>
            <th style={thStyle}>Channel</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Users</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>%</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {list.map((c) => (
            <tr key={c.key}>
              <td style={tdStyle}>
                <div style={{ fontWeight: 500 }}>{c.label}</div>
                <div style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>{c.key}</div>
              </td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>{c.count.toLocaleString()}</td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>{(c.percent ?? 0).toFixed(1)}%</td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => onView(c.key)}
                >
                  View users →
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Funnel Tab ────────────────────────────────────────── */

function FunnelTab({ funnel, days, onDaysChange }) {
  const DAYS_OPTIONS = [7, 30, 60, 90, 365];

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={sectionTitleStyle}>Conversion funnel</div>
        <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: 999, padding: 3 }}>
          {DAYS_OPTIONS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => onDaysChange(d)}
              style={{
                background: days === d ? '#fff' : 'transparent',
                color: days === d ? '#0f172a' : '#64748b',
                border: 'none', padding: '5px 12px', borderRadius: 999,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                boxShadow: days === d ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              }}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {!funnel && <div>Loading funnel…</div>}
      {funnel && (
        <>
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 6, flexWrap: 'wrap' }}>
            {(funnel.stages || []).map((s, i) => {
              const prev = i > 0 ? funnel.stages[i - 1] : null;
              const dropPct = prev ? ((1 - (s.count / Math.max(prev.count, 1))) * 100) : null;
              return (
                <React.Fragment key={s.key}>
                  {i > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 70, padding: '0 4px' }}>
                      <div style={{ fontSize: 18, color: '#cbd5e1', lineHeight: 1 }}>→</div>
                      <div style={{ fontSize: 11, color: dropPct >= 50 ? '#dc2626' : '#64748b', marginTop: 4, fontWeight: 600 }}>
                        {s.pct_from_prev != null ? `${s.pct_from_prev.toFixed(1)}%` : (dropPct != null ? `${(100 - dropPct).toFixed(1)}%` : '—')}
                      </div>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>retained</div>
                    </div>
                  )}
                  <div
                    style={{
                      flex: '1 1 140px', minWidth: 140,
                      background: i === 0 ? '#eff6ff' : '#f8fafc',
                      border: `1px solid ${i === 0 ? '#bfdbfe' : '#e2e8f0'}`,
                      borderRadius: 8, padding: 16, textAlign: 'center',
                    }}
                  >
                    <div style={{ ...tileLabelStyle, marginBottom: 8 }}>{s.label}</div>
                    <div style={{ fontSize: 30, fontWeight: 700, color: '#0f172a' }}>{(s.count ?? 0).toLocaleString()}</div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
          <div style={{ marginTop: 16, fontSize: 12, color: '#64748b' }}>
            Window: last {funnel.window_days ?? days} day{(funnel.window_days ?? days) === 1 ? '' : 's'}. Each stage counts users in the window who reached at least that step.
          </div>
        </>
      )}
    </div>
  );
}
